import type { AgencyId, SuretyAccountId } from "@inrev/common";
import { BondRequestSubmissionModal } from "@portal/components/features/bond-request-submission";
import { Button } from "@portal/components/primitives/Button";
import { GlobalSearchButton } from "@portal/components/ui/GlobalSearch/GlobalSearchButton";
import { PopoverWithCarat } from "@portal/components/ui/PopoverWithCarat";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { useCallback, useState } from "react";
import { AccountPopoverButtons } from "../account/create/AccountPopoverButtons";
import { AdminAccountCreateController } from "../account/create/quick-submit/AdminAccountCreateController";
import { BondRequestQuickSubmitController } from "../request/create/quick-submit/BondRequestQuickSubmitController";

type PopoverView = "selection" | "quickSubmit";

type AdminHeaderActionButtonsProps = {
	accountId?: SuretyAccountId;
	agencyId?: AgencyId;
};

export const AdminHeaderActionButtons = ({
	accountId,
	agencyId,
}: AdminHeaderActionButtonsProps = {}) => {
	const [openPopover, setOpenPopover] = useState<"bond" | "account" | null>(null);
	const [popoverView, setPopoverView] = useState<PopoverView>("selection");
	const [submissionType, setSubmissionType] = useState<"prequal" | "combined">("prequal");
	const [extractionModalOpen, setExtractionModalOpen] = useState(false);

	const aiExtractionEnabled = useFeatureFlag("ai_bond_request_extraction");
	const bondRequestExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const siteHeaderExtractionEnabled = aiExtractionEnabled && bondRequestExtractionEnabled;

	const handlePopoverToggle = (popover: "bond" | "account") => {
		setOpenPopover((prev) => {
			if (prev === popover) {
				setPopoverView("selection");
				return null;
			}
			return popover;
		});
	};

	const handleSelectSubmissionType = (type: "prequal" | "combined") => {
		setSubmissionType(type);
		setPopoverView("quickSubmit");
	};

	const handleClose = useCallback(() => {
		setOpenPopover(null);
		setPopoverView("selection");
	}, []);

	return (
		<div className="flex items-center gap-3">
			<GlobalSearchButton />
			{siteHeaderExtractionEnabled ? (
				<Button
					data-testid="popover-bond-request-button"
					color="primary"
					variant="outline"
					onClick={() => setExtractionModalOpen(true)}
					trackingId="bond-request-admin-entry-extraction"
				>
					Bond Request
				</Button>
			) : (
				<PopoverWithCarat
					buttonComponent={
						<Button
							data-testid="popover-bond-request-button"
							color="primary"
							variant="outline"
							trackingId="bond-request-admin-entry-quick"
						>
							Bond Request
						</Button>
					}
					anchorLocation={["bottom"]}
					isOpen={openPopover === "bond"}
					onToggle={() => handlePopoverToggle("bond")}
				>
					<BondRequestQuickSubmitController onClose={handleClose} />
				</PopoverWithCarat>
			)}
			<PopoverWithCarat
				buttonComponent={
					<Button
						data-testid="popover-account-submission-button"
						color="primary"
						variant="outline"
						trackingId="account-admin-entry-menu"
					>
						New Submission
					</Button>
				}
				anchorLocation={["bottom"]}
				isOpen={openPopover === "account"}
				onToggle={() => handlePopoverToggle("account")}
			>
				{popoverView === "selection" ? (
					<AccountPopoverButtons
						prequalOnClick={() => handleSelectSubmissionType("prequal")}
						withBondRequestOnClick={() => handleSelectSubmissionType("combined")}
					/>
				) : (
					<AdminAccountCreateController
						isCombined={submissionType === "combined"}
						onClose={handleClose}
					/>
				)}
			</PopoverWithCarat>
			{extractionModalOpen && (
				<BondRequestSubmissionModal
					isAdmin
					accountId={accountId}
					agencyId={agencyId}
					onClose={() => setExtractionModalOpen(false)}
				/>
			)}
		</div>
	);
};
